// ═══════════════════════════════════════════
// MonArgent Pro — Notification System
// Works on: PWA (push), Electron (desktop), and in-app
// ═══════════════════════════════════════════

// Check if running in Electron
const isElectron = () => window.electronAPI?.isElectron === true;

// ─── Request notification permission ───
export async function requestPermission() {
  if (isElectron()) return true; // Electron always has permission

  if (!("Notification" in window)) {
    console.log("[Notif] Not supported in this browser");
    return false;
  }

  if (Notification.permission === "granted") return true;

  const permission = await Notification.requestPermission();
  return permission === "granted";
}

// ─── Send a notification ───
export async function sendNotification(title, body, options = {}) {
  try {
    if (isElectron()) {
      // Use Electron's native notifications
      await window.electronAPI.showNotification(title, body);
      return true;
    }

    // PWA / Browser notification
    if (!("Notification" in window)) return false;
    if (Notification.permission !== "granted") {
      const ok = await requestPermission();
      if (!ok) return false;
    }

    // Use service worker for persistent notifications
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(title, {
        body,
        icon: "/icon-192.png",
        badge: "/icon-192.png",
        vibrate: [100, 50, 100],
        tag: options.tag || "monargent",
        renotify: options.renotify || false,
        ...options,
      });
    } else {
      // Fallback to basic notification
      new Notification(title, { body, icon: "/icon-192.png" });
    }
    return true;
  } catch (err) {
    console.error("[Notif] Error:", err);
    return false;
  }
}

// ─── Pre-built notification types ───
export function notifyVente(amount, category) {
  return sendNotification(
    "💵 Vente enregistrée !",
    `${category} — ${formatAmount(amount)} FCFA`,
    { tag: "vente" }
  );
}

export function notifyDepense(amount, category) {
  return sendNotification(
    "💸 Dépense enregistrée",
    `${category} — ${formatAmount(amount)} FCFA`,
    { tag: "depense" }
  );
}

export function notifyDailyReport(ventes, depenses) {
  const benef = ventes - depenses;
  return sendNotification(
    "📊 Bilan du jour",
    `Ventes: ${formatAmount(ventes)} F | Dépenses: ${formatAmount(depenses)} F | Bénéfice: ${formatAmount(benef)} F`,
    { tag: "report" }
  );
}

export function notifyAlert(title, message) {
  return sendNotification(`⚠️ ${title}`, message, { tag: "alert", renotify: true });
}

export function notifyEmployeeAlert(empName, message) {
  return sendNotification(
    `👤 ${empName}`,
    message,
    { tag: `emp-${empName}` }
  );
}

// ─── Scheduled alerts (check conditions) ───
export function checkAlerts(stats, config) {
  const alerts = [];

  // High expenses alert
  if (stats.totalDepenses > stats.totalVentes * 0.8) {
    alerts.push({
      type: "warning",
      icon: "⚠️",
      title: "Dépenses élevées !",
      message: `Les dépenses atteignent ${Math.round((stats.totalDepenses / Math.max(stats.totalVentes, 1)) * 100)}% des ventes`,
    });
  }

  // Negative profit alert
  if (stats.benefice < 0) {
    alerts.push({
      type: "danger",
      icon: "🔴",
      title: "Bénéfice négatif",
      message: `Vous perdez ${formatAmount(Math.abs(stats.benefice))} FCFA aujourd'hui`,
    });
  }

  // No sales today
  if (stats.nbVentes === 0 && new Date().getHours() >= 12) {
    alerts.push({
      type: "info",
      icon: "📭",
      title: "Pas de vente enregistrée",
      message: "N'oubliez pas de noter vos ventes !",
    });
  }

  // Good day!
  if (stats.benefice > 0 && stats.nbTransactions >= 5) {
    alerts.push({
      type: "success",
      icon: "🎉",
      title: "Bonne journée !",
      message: `Bénéfice de ${formatAmount(stats.benefice)} FCFA avec ${stats.nbTransactions} transactions`,
    });
  }

  return alerts;
}

// ─── In-app toast notifications ───
let toastContainer = null;

export function showToast(message, type = "info", duration = 3000) {
  if (!toastContainer) {
    toastContainer = document.createElement("div");
    toastContainer.id = "monargent-toasts";
    toastContainer.style.cssText = `
      position: fixed; top: 16px; left: 50%; transform: translateX(-50%);
      z-index: 99999; display: flex; flex-direction: column; gap: 8px;
      pointer-events: none; max-width: 360px; width: calc(100% - 32px);
    `;
    document.body.appendChild(toastContainer);
  }

  const colors = {
    success: { bg: "#DCFCE7", border: "#22C55E", text: "#166534", icon: "✅" },
    warning: { bg: "#FEF3C7", border: "#F59E0B", text: "#92400E", icon: "⚠️" },
    danger: { bg: "#FEE2E2", border: "#EF4444", text: "#991B1B", icon: "🔴" },
    info: { bg: "#E0F2FE", border: "#0EA5E9", text: "#0C4A6E", icon: "ℹ️" },
  };
  const c = colors[type] || colors.info;

  const toast = document.createElement("div");
  toast.style.cssText = `
    background: ${c.bg}; border: 1px solid ${c.border}33; border-left: 4px solid ${c.border};
    border-radius: 12px; padding: 12px 16px; font-family: 'Nunito', sans-serif;
    font-size: 13px; font-weight: 600; color: ${c.text};
    box-shadow: 0 4px 20px rgba(0,0,0,0.1); pointer-events: auto;
    animation: slideDown 0.3s ease; display: flex; align-items: center; gap: 8px;
  `;
  toast.innerHTML = `<span>${c.icon}</span><span>${message}</span>`;
  toastContainer.appendChild(toast);

  setTimeout(() => {
    toast.style.animation = "slideUp 0.3s ease forwards";
    setTimeout(() => toast.remove(), 300);
  }, duration);
}

function formatAmount(n) {
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

// Add toast animations
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = `
    @keyframes slideDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes slideUp { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(-20px); } }
  `;
  document.head.appendChild(style);
}
